import { useMutation } from '@tanstack/react-query';

import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import useEditableQuestion from '@/data/hook/question/useEditableQuestion';
import {
  createTextQuestionIntoActivity,
  updateTextQuestionIntoActivity,
} from '@/data/services/question/textQuestionServices';
import Activity from '@/models/Activity';
import { Question, TextQuestion } from '@/models/Question';
import {
  adapterFeedbacks,
  sanitizeFeedbacks,
} from '@/utils/questions/adapterFeedbacks';
import { getQuestionErrorMessage } from '@/utils/questions/getQuestionErrorMessage';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import alert from '@/utils/shared/alert';
import { isEqual } from 'lodash';
import MultimediaForm, { MultimediaFormFields } from './MultimediaForm';

type MultimediaFormServiceProps = {
  activity: Activity;
  question: Question;
  isActive?: boolean;
};

export default function MultimediaFormService({
  activity,
  question,
  isActive,
}: MultimediaFormServiceProps) {
  const gradeCtx = useQuestionExamGradeCtx();
  const { ensureEditable, resolveEditable } = useEditableQuestion(activity);

  const handleCreate = async (
    data: MultimediaFormFields,
  ): Promise<TextQuestion> => {
    const { maxGrade, feedbacks, ...rest } = data;
    const editableActivity = await ensureEditable();
    const saved = await createTextQuestionIntoActivity(editableActivity.id, {
      type: question.type,
      identifier: question.identifier,
      order: question.order,
      feedbacks: sanitizeFeedbacks(feedbacks),
      ...rest,
    });
    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const handleUpdate = async (
    data: MultimediaFormFields,
  ): Promise<TextQuestion> => {
    const { maxGrade, ...rest } = data;
    const oldValues: Omit<MultimediaFormFields, 'maxGrade'> = {
      title: normalizeTranslatableField(question.title),
      context: normalizeTranslatableField(question.context),
      feedbacks: adapterFeedbacks(question.feedbacks ?? []),
    };

    const changedFields: Partial<Omit<MultimediaFormFields, 'maxGrade'>> = {};
    if (!isEqual(oldValues.title, rest.title)) {
      changedFields.title = rest.title;
    }
    if (!isEqual(oldValues.context, rest.context)) {
      changedFields.context = rest.context;
    }
    const newSanitizedFeedbacks = sanitizeFeedbacks(rest.feedbacks);
    const oldSanitizedFeedbacks = sanitizeFeedbacks(oldValues.feedbacks);
    if (!isEqual(oldSanitizedFeedbacks, newSanitizedFeedbacks)) {
      changedFields.feedbacks = newSanitizedFeedbacks;
    }

    const { updateTarget } = await resolveEditable(question as TextQuestion);
    const saved = await updateTarget(
      changedFields,
      updateTextQuestionIntoActivity,
    );

    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: (data: MultimediaFormFields) =>
      !question.id ? handleCreate(data) : handleUpdate(data),
    onError: err => alert.error(getQuestionErrorMessage(err)),
  });

  return (
    <MultimediaForm
      key={question.identifier}
      isActive={isActive}
      question={question}
      onSubmit={submitQuestion}
      isLoading={isPending}
    />
  );
}
